import { useEffect } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { ATT_LOGOS, ATT_BLUE, findDevice } from "@/attData";
import { LOGO_URL } from "@/data";

const SITE = "https://heritagefamilypa.com";

const BASE_META = {
  title: "AT&T Wireless & Internet | Heritage Family Protection Agency — AT&T Authorized Dealer",
  description: "Residential & business AT&T wireless, fiber internet, phones, tablets and watches — pre-order through Heritage Family Protection Agency, your AT&T Authorized Dealer.",
  image: `${SITE}/att/heritage-att-banner.png`,
};

const PATH_META = {
  "/att/devices": {
    title: "Shop AT&T Phones, Tablets & Watches | Heritage — AT&T Authorized Dealer",
    description: "Browse the latest iPhone, Galaxy, Pixel and Motorola devices — pick your color, storage, and installment plan, then pre-order through Heritage Family Protection Agency.",
    image: `${SITE}/att/og-devices.jpg`,
  },
  "/att/plans": {
    title: "AT&T Unlimited Wireless Plans & Pricing | Heritage — AT&T Authorized Dealer",
    description: "Compare AT&T unlimited wireless plans for home and business — 5G access, hotspot data, and multi-line savings through Heritage Family Protection Agency.",
    image: `${SITE}/att/og-plans.jpg`,
  },
  "/att/internet": {
    title: "AT&T Fiber Internet & Internet Air | Heritage — AT&T Authorized Dealer",
    description: "AT&T Fiber from 300 Mbps to 5 GIG plus wireless Internet Air — residential and business pricing through Heritage Family Protection Agency.",
    image: `${SITE}/att/og-internet.jpg`,
  },
  "/att/preorder": {
    title: "Pre-Order AT&T Devices & Service | Heritage — AT&T Authorized Dealer",
    description: "Start your residential or business AT&T pre-order — devices, plans, trade-ins and porting. No payment now; we confirm every promo with you first.",
    image: `${SITE}/att/og-preorder.jpg`,
  },
};

const metaFor = (pathname) => {
  if (pathname.startsWith("/att/devices/")) {
    const d = findDevice(pathname.split("/")[3]);
    if (d) {
      return {
        title: `${d.name} — Colors, Storage & Installments | Heritage AT&T Authorized Dealer`,
        description: `Customize the ${d.name}: pick your color, storage, installment plan, trade-in, add-ons and insurance — then pre-order through Heritage Family Protection Agency.`,
        image: `${SITE}${d.img}`,
        url: `${SITE}${pathname}`,
      };
    }
  }
  const m = PATH_META[pathname] || BASE_META;
  return { ...m, url: `${SITE}${pathname}` };
};

const NAV = [
  { to: "/att", label: "Home", end: true },
  { to: "/att/devices", label: "Devices" },
  { to: "/att/plans", label: "Wireless Plans" },
  { to: "/att/internet", label: "Internet" },
  { to: "/att/preorder", label: "Pre-Order" },
  { to: "/careers/att-apply", label: "Careers" },
];

export default function ATTLayout() {
  const { pathname } = useLocation();
  useEffect(() => {
    const META = metaFor(pathname);
    const prevTitle = document.title;
    document.title = META.title;
    const set = (sel, val) => {
      const el = document.querySelector(sel);
      if (!el) return null;
      const old = el.getAttribute("content");
      el.setAttribute("content", val);
      return () => el.setAttribute("content", old);
    };
    const undos = [
      set('meta[property="og:title"]', META.title),
      set('meta[property="og:description"]', META.description),
      set('meta[property="og:url"]', META.url),
      set('meta[property="og:image"]', META.image),
      set('meta[property="og:image:secure_url"]', META.image),
      set('meta[property="og:image:alt"]', META.title),
      set('meta[name="description"]', META.description),
      set('meta[name="twitter:title"]', META.title),
      set('meta[name="twitter:description"]', META.description),
      set('meta[name="twitter:image"]', META.image),
    ].filter(Boolean);
    return () => {
      document.title = prevTitle;
      undos.forEach((u) => u());
    };
  }, [pathname]);

  return (
    <div className="bg-[#f4f6f8]" data-testid="att-site">
      <div className="h-24 bg-[#06111f]" />
      <div className="bg-white shadow-md sticky top-0 z-30" data-testid="att-subheader">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-4 md:gap-6 py-3 flex-wrap">
            <Link to="/att" className="inline-flex items-center rounded-xl bg-[#06111f] px-4 py-2">
              <img src={LOGO_URL} alt="Heritage Family Protection Agency" className="h-8 md:h-10 w-auto object-contain" data-testid="att-subheader-heritage-logo" />
            </Link>
            <Link to="/att" className="flex flex-col items-center">
              <img src={ATT_LOGOS.att} alt="AT&T" className="h-8 md:h-10 w-auto object-contain" data-testid="att-subheader-logo" />
              <p className="mt-0.5 font-body text-[8px] md:text-[9px] font-bold tracking-[0.3em] uppercase text-[#1d2329]">Authorized Dealer</p>
            </Link>
          </div>
          <nav className="flex items-center justify-center gap-4 md:gap-8 pb-3 flex-wrap" data-testid="att-site-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`att-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-body text-sm md:text-base font-semibold pb-1 border-b-2 transition-colors ${isActive ? "border-[#00A8E0] text-[#00A8E0]" : "border-transparent text-[#1d2329] hover:text-[#00A8E0]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <Outlet />

      <div className="py-10 bg-[#06111f]">
        <p className="max-w-5xl mx-auto px-6 md:px-12 font-body text-[11px] text-white/40 leading-relaxed">
          AT&amp;T, the AT&amp;T logo, and all related marks are trademarks of AT&amp;T Intellectual Property. Offers
          subject to change; eligibility, trade-in values, and credits determined by AT&amp;T at time of order.
          Heritage Family Protection Agency operates as an AT&amp;T Authorized Dealer through DM Wireless Connect.
        </p>
      </div>
    </div>
  );
}
